import React, { useEffect } from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  ArrowUpRight,
  Clock3,
  Leaf,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";

import { fetchRestaurantFooter } from "../../actions/footerActions";

import "./css/Footer.css";


function Footer() {

  /* =====================================================
     ROUTER
  ===================================================== */

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const {
    restaurantId,
    tableId,
  } = useParams();


  /* =====================================================
     RESTAURANT
  ===================================================== */

  const dispatch = useDispatch();
  const footerData = useSelector((state) => state.footer?.data || {});

  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchRestaurantFooter(restaurantId)).catch(() => {});
    }
  }, [dispatch, restaurantId]);

  const restaurantName = footerData.restaurant_name || "Restaurant";
  const description = footerData.description || "";
  const address = footerData.address || "";
  const phone = footerData.phone || "";
  const email = footerData.email || "";
  const openingHours = footerData.opening_hours || "";

  /* =====================================================
     YEAR
  ===================================================== */

  const currentYear =
    new Date().getFullYear();


  /* =====================================================
     HOME PATH
  ===================================================== */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  /* =====================================================
     GO HOME
  ===================================================== */

  const goHome = () => {

    /*
     * Already on homepage.
     */

    if (
      location.pathname === homePath
    ) {

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }


    /*
     * Coming from cart,
     * checkout, payment, etc.
     */

    navigate(homePath);

  };


  /* =====================================================
     SECTION NAVIGATION
  ===================================================== */

  const scrollToSection = (
    sectionId
  ) => {

    /*
     * Already on homepage.
     */

    if (
      location.pathname === homePath
    ) {

      const element =
        document.getElementById(
          sectionId
        );


      if (element) {

        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

      }

      return;
    }


    /*
     * Coming from another screen.
     *
     * Header.js already handles
     * hashes when HomeScreen loads.
     */

    navigate(
      `${homePath}#${sectionId}`
    );

  };


  /* =====================================================
     FOOTER
  ===================================================== */

  return (

    <footer
      className="theme9-footer"
      id="theme9-contact"
    >


      {/* =================================================
          DECORATIVE BACKGROUND
      ================================================= */}

      <div
        className="
          theme9-footer-decoration
          theme9-footer-decoration-one
        "
      />

      <div
        className="
          theme9-footer-decoration
          theme9-footer-decoration-two
        "
      />


      {/* =================================================
          MAIN FOOTER
      ================================================= */}

      <div className="theme9-footer-main">


        {/* =================================================
            BRAND
        ================================================= */}

        <div className="theme9-footer-brand">

          <button
            type="button"
            className="theme9-footer-logo"
            onClick={goHome}
            aria-label="Go to home"
          >

            <span className="theme9-footer-logo-icon">

              <Leaf
                size={20}
              />

            </span>


            <span className="theme9-footer-logo-name">
              {restaurantName}
            </span>


            <span className="theme9-footer-logo-dot">
              .
            </span>

          </button>


          <p className="theme9-footer-description">{description}</p>


          <div className="theme9-footer-fresh">

            <span>
              <Leaf
                size={14}
              />
            </span>

            Fresh every day

          </div>

        </div>


        {/* =================================================
            EXPLORE
        ================================================= */}

        <div className="theme9-footer-column">

          <h3>
            Explore
          </h3>


          <button
            type="button"
            onClick={goHome}
          >
            Home

            <ArrowUpRight
              size={14}
            />
          </button>


          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme9-popular"
              )
            }
          >
            Popular

            <ArrowUpRight
              size={14}
            />
          </button>


          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme9-about"
              )
            }
          >
            About Us

            <ArrowUpRight
              size={14}
            />
          </button>


          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme9-menu"
              )
            }
          >
            Our Menu

            <ArrowUpRight
              size={14}
            />
          </button>

        </div>


        {/* =================================================
            CONTACT
        ================================================= */}

        <div className="theme9-footer-column">

          <h3>
            Contact
          </h3>


          {address && (
            <div className="theme9-footer-contact-item">
              <span className="theme9-footer-contact-icon"><MapPin size={17} /></span>
              <p>{address}</p>
            </div>
          )}
          {phone && (
            <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="theme9-footer-contact-item">
              <span className="theme9-footer-contact-icon"><Phone size={16} /></span>
              <span>{phone}</span>
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} className="theme9-footer-contact-item">
              <span className="theme9-footer-contact-icon"><Mail size={16} /></span>
              <span>{email}</span>
            </a>
          )}

        </div>


        {/* =================================================
            OPENING HOURS
        ================================================= */}

        <div className="theme9-footer-column">

          <h3>
            Opening Hours
          </h3>


          {openingHours && (
            <div className="theme9-footer-hours">
              <div className="theme9-footer-hours-row">
                <div className="theme9-footer-hours-title">
                  <Clock3 size={15} />
                  <span>{openingHours}</span>
                </div>
              </div>
            </div>
          )}

        </div>


      </div>


      {/* =================================================
          DIVIDER
      ================================================= */}

      <div className="theme9-footer-divider" />


      {/* =================================================
          BOTTOM FOOTER
      ================================================= */}

      <div className="theme9-footer-bottom">


        <p>
          © {currentYear}{" "}
          {restaurantName}.
          All rights reserved.
        </p>


        <small className="theme9-footer-disclaimer">
          Food images are for presentation purposes only. Actual items may vary.
        </small>

        <div className="theme9-footer-bottom-links">

          <button
            type="button"
          >
            Privacy
          </button>

          <button
            type="button"
          >
            Terms
          </button>

        </div>


      </div>


    
      {/* Restaurant social profiles (only show configured HTTPS/HTTP URLs) */}
      {[
        { field: "facebook_url", label: "Facebook", glyph: "f" },
        { field: "instagram_url", label: "Instagram", glyph: "◎" },
        { field: "youtube_url", label: "YouTube", glyph: "▶" },
        { field: "whatsapp_url", label: "WhatsApp", glyph: "◉" },
      ].some(({ field }) => /^https?:\/\//i.test((footerData?.[field] || "").trim())) && (
        <nav aria-label="Restaurant social media" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: "14px", padding: "14px 18px" }}>
          {[
            { field: "facebook_url", label: "Facebook", glyph: "f" },
            { field: "instagram_url", label: "Instagram", glyph: "◎" },
            { field: "youtube_url", label: "YouTube", glyph: "▶" },
            { field: "whatsapp_url", label: "WhatsApp", glyph: "◉" },
          ].filter(({ field }) => /^https?:\/\//i.test((footerData?.[field] || "").trim())).map(({ field, label, glyph }) => (
            <a key={field} href={footerData[field].trim()} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}
               style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "32px", minHeight: "32px", color: "inherit", textDecoration: "none", fontWeight: 700, fontSize: "20px" }}>
              {glyph}
            </a>
          ))}
        </nav>
      )}
    </footer>

  );

}


export default Footer;